import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  /**
   * Gets the cart count without the gift wrapping product.
   */
  getVisibleCartCount = async () => {
    const response = await fetch(window.Shopify.routes.root + 'cart.js');

    if (!response.ok) {
      throw new Error('Could not fetch cart');
    }

    const cart = await response.json();

    return cart.items
      .filter((item) => item.handle !== 'cadeauverpakking')
      .reduce((total, item) => total + item.quantity, 0);
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    try {
      const itemCount = await this.getVisibleCartCount();

      this.renderCartBubble(itemCount, false);
    } catch (_) {
      // Fallback to Horizon's normal behaviour
      const itemCount = event.detail.data?.itemCount ?? 0;
      const comingFromProductForm =
        event.detail.data?.source === 'product-form-component';

      this.renderCartBubble(itemCount, comingFromProductForm);
    }
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (
    itemCount,
    comingFromProductForm,
    animate = true
  ) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle(
      'visually-hidden',
      itemCount === 0
    );
    this.refs.cartBubble.classList.toggle(
      'cart-bubble--animating',
      itemCount > 0 && animate
    );

    this.currentCartCount = comingFromProductForm
      ? this.currentCartCount + itemCount
      : itemCount;

    this.classList.toggle(
      'header-actions__cart-icon--has-cart',
      itemCount > 0
    );

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate) return;

    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = async () => {
    try {
      const itemCount = await this.getVisibleCartCount();

      this.renderCartBubble(itemCount, false, false);
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}